import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import {
  COLUMN_LEVEL_LABELS,
  type ColumnLevel,
  isColumnLevel,
  type SecondaryDimensionValues,
  secondaryDimensionOptions,
} from '../../PageConfigProvider/types/dimension';
import type { DimensionSource } from '../../PageConfigProvider/types/item';
import {
  isCompositionMetric,
  isPayMetric,
  type MetricValues,
} from '../../PageConfigProvider/types/metric';

export type DimensionSourceOption = {
  value: DimensionSource;
  label: string;
};

export const getAllDimensionSourceOptions = ({
  chartType,
  primaryFilterId,
  metric,
}: {
  chartType?: ChartType;
  primaryFilterId: PrimaryFilterId;
  metric?: MetricValues;
}): DimensionSourceOption[] => {
  if (chartType !== 'bar' && chartType !== 'stacked-bar') {
    return [];
  }

  const primaryDimensionLabel = getPrimaryDimensionLabel(primaryFilterId);

  if (chartType === 'stacked-bar') {
    return [
      { value: 'primary', label: primaryDimensionLabel },
      { value: 'timeseries', label: 'Timeseries' },
    ];
  }

  const allowedDimensions = secondaryDimensionOptions
    .map((option) => option.value)
    .filter(filterSecondaryDimensionByPrimaryDimension(primaryFilterId))
    .filter(filterSecondaryDimensionsByMetric(metric));

  return [
    { value: 'primary', label: primaryDimensionLabel },
    ...allowedDimensions.map((dimension) => ({
      value: dimension,
      label: mapDimensionValueToLabel(dimension),
    })),
  ];
};

const mapDimensionValueToLabel = (
  dimension: SecondaryDimensionValues
): string => {
  switch (dimension) {
    case 'rics':
      return 'Top Industries';
    case 'company':
      return 'Top Companies';
    case 'geography':
      return 'Top Geographies';
    case 'role':
      return 'Top Roles';
    case 'skills':
      return 'Top Skills';
    case 'activities':
      return 'Top Activities';
    case 'competitors':
      return 'Top Competitors';
    case 'subsidiaries':
      return 'Subsidiaries';
    case 'education':
      return 'Education';
    case 'seniority':
      return 'Seniority';
    case 'gender':
      return 'Gender';
    case 'ethnicity':
      return 'Ethnicity';
    case 'timeseries':
      return 'Time';
  }
};

/** Label for the page's primary entity dimension (e.g. "Company"). */
const getPrimaryDimensionLabel = (
  primaryFilterId: PrimaryFilterId | undefined
): string => {
  switch (primaryFilterId) {
    case FilterId.COMPANY:
      return 'Company';
    case FilterId.INDUSTRY:
      return 'Industry';
    case FilterId.GEOGRAPHY:
      return 'Geography';
    case FilterId.ROLE:
      return 'Role';
    case FilterId.CUSTOM_SKILL:
      return 'Skill';
    case FilterId.ACTIVITY:
      return 'Activity';
    default:
      return 'Primary';
  }
};

/** Resolve a display label for any table dimension-column value. */
export const getDimensionLabel = (
  dimension: DimensionSource | ColumnLevel,
  primaryFilterId?: PrimaryFilterId
): string => {
  if (dimension === 'primary') return getPrimaryDimensionLabel(primaryFilterId);
  return isColumnLevel(dimension)
    ? COLUMN_LEVEL_LABELS[dimension]
    : mapDimensionValueToLabel(dimension);
};

const filterSecondaryDimensionByPrimaryDimension =
  (primaryDimension: PrimaryFilterId) =>
  (secondaryDimension: SecondaryDimensionValues): boolean => {
    const allowedDimensions = ((): SecondaryDimensionValues[] => {
      switch (primaryDimension) {
        // COMPANY allowed alternate dimensions
        case FilterId.COMPANY:
          return [
            'geography',
            'role',
            'rics',
            'skills',
            'seniority',
            'education',
            'ethnicity',
            'gender',
            'competitors',
          ];
        // INDUSTRY allowed alternate dimensions
        case FilterId.INDUSTRY:
          return [
            'company',
            'geography',
            'role',
            'skills',
            'seniority',
            'education',
            'ethnicity',
            'gender',
          ];
        // GEOGRAPHY allowed alternate dimensions
        case FilterId.GEOGRAPHY:
          return [
            'rics',
            'company',
            'role',
            'skills',
            'seniority',
            'education',
            'ethnicity',
            'gender',
          ];
        // ROLE allowed alternate dimensions
        case FilterId.ROLE:
          return [
            'rics',
            'geography',
            'skills',
            'seniority',
            'education',
            'ethnicity',
            'gender',
          ];
        // SKILL allowed alternate dimensions: the role list with role in
        // place of skills (the skill templates show a Role breakdown where
        // the role templates show a Skill one).
        case FilterId.CUSTOM_SKILL:
          return [
            'rics',
            'geography',
            'role',
            'seniority',
            'education',
            'ethnicity',
            'gender',
          ];
        // ACTIVITY: both role and skill are breakdowns on activity pages.
        case FilterId.ACTIVITY:
          return [
            'rics',
            'geography',
            'role',
            'skills',
            'seniority',
            'education',
            'ethnicity',
            'gender',
          ];
      }
    })();

    return allowedDimensions.includes(secondaryDimension);
  };

const filterSecondaryDimensionsByMetric =
  (metric: MetricValues | undefined) =>
  (secondaryDimension: SecondaryDimensionValues): boolean => {
    const allowedDimensions = (() => {
      if (isCompositionMetric(metric)) {
        // HEADCOUNT allowed alternate dimensions
        if (metric === 'headcount') {
          return [
            'rics',
            'company',
            'competitors',
            'geography',
            'role',
            'skills',
            'seniority',
            'education',
            'ethnicity',
            'gender',
          ];
        }
        // OTHER COMPOSITION METRICS allowed alternate dimensions
        return ['company', 'competitors', 'geography'];
      }
      if (isPayMetric(metric)) {
        // PAY METRICS allowed alternate dimensions
        return ['competitors'];
      }
      // OTHER Metrics currently do not support alternate dimensions
      return [];
    })();

    return allowedDimensions.includes(secondaryDimension);
  };
